import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../components/StyledTableComponents';
import {
  TPaymentTerminalProvider,
  TRestaurantPaymentConfig,
} from '../../../PaymentProviders/schemas/payment-provider-schema';
import { getConfigHealth, resolveEnv } from '../../../PaymentProviders/utils';

type RestaurantConfigsListProps = {
  configs: TRestaurantPaymentConfig[];
  /** Catalog rows, used to read each provider's `required` client-field flags. */
  providers: TPaymentTerminalProvider[];
  onEdit: (config: TRestaurantPaymentConfig) => void;
  onDelete: (config: TRestaurantPaymentConfig) => void;
  onSetDefault: (config: TRestaurantPaymentConfig) => void;
  settingDefaultId?: string | null;
};

const RestaurantConfigsList = ({
  configs,
  providers,
  onEdit,
  onDelete,
  onSetDefault,
  settingDefaultId,
}: RestaurantConfigsListProps) => {
  const env = resolveEnv();
  if (!configs || configs.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>
          No payment provider configs yet. Add one to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Provider</StyledTableCell>
            <StyledTableCell>Provider ID</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>Config Version</StyledTableCell>
            <StyledTableCell>Default</StyledTableCell>
            <StyledTableCell>Updated</StyledTableCell>
            <StyledTableCell align="right">Actions</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {configs.map((config) => {
            const provider = providers.find((p) => p.providerId === config.providerId);
            const { missingRequired, envWarnings, isReady } = getConfigHealth(
              config,
              provider,
              env,
            );

            return (
            <StyledTableRow key={config.providerId}>
              <StyledTableCell sx={styles.providerCell}>
                {config.displayName || config.providerId}
              </StyledTableCell>
              <StyledTableCell sx={styles.monoCell}>{config.providerId}</StyledTableCell>
              <StyledTableCell>
                {envWarnings.length > 0 ? (
                  <Tooltip
                    title={`Key shape is wrong for ${env}: ${envWarnings
                      .map((w) => `${w.fieldId} starts with ${w.prefix}`)
                      .join(', ')}. The kiosk will silently drop this provider.`}
                  >
                    <Chip label="Env mismatch" size="small" sx={styles.errorChip} />
                  </Tooltip>
                ) : missingRequired.length > 0 ? (
                  <Tooltip
                    title={`Missing required field${
                      missingRequired.length > 1 ? 's' : ''
                    }: ${missingRequired.join(', ')}. The kiosk treats this as not configured and will refuse card payment.`}
                  >
                    <Chip label="Incomplete" size="small" sx={styles.warningChip} />
                  </Tooltip>
                ) : (
                  <Chip label="Ready" size="small" sx={styles.readyChip} />
                )}
              </StyledTableCell>
              <StyledTableCell>v{config.configVersion}</StyledTableCell>
              <StyledTableCell>
                {config.isActiveDefault ? (
                  isReady ? (
                    <Chip
                      label="Default"
                      size="small"
                      icon={<StarIcon style={{ color: '#f9a825' }} />}
                      sx={styles.defaultChip}
                    />
                  ) : (
                    <Tooltip title="Marked default, but this provider will be dropped — the kiosk will receive defaultProvider: null and pre-select nothing.">
                      <Chip
                        label="Default — not usable"
                        size="small"
                        icon={<WarningAmberIcon style={{ color: '#b91c1c' }} />}
                        sx={styles.errorChip}
                      />
                    </Tooltip>
                  )
                ) : (
                  <Tooltip title="Set as default">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onSetDefault(config)}
                        disabled={settingDefaultId === config.providerId}
                      >
                        <StarOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </StyledTableCell>
              <StyledTableCell sx={styles.dateCell}>
                {config.updatedAt
                  ? new Date(config.updatedAt).toLocaleString()
                  : '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                <Box sx={styles.actionBox}>
                  <Tooltip title="Edit client fields">
                    <IconButton size="small" onClick={() => onEdit(config)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete config">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(config)}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>
            </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RestaurantConfigsList;

const styles = {
  tableContainer: {
    boxShadow: 'none',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    borderRadius: '8px',
    height: '6rem',
  },
  emptyText: {
    color: '#999',
    fontSize: '14px',
    fontFamily: 'InterRegular',
  },
  providerCell: {
    fontFamily: 'GilroySemiBold',
  },
  readyChip: {
    backgroundColor: '#e8f5e9',
    color: '#1b5e20',
    fontFamily: 'GilroySemiBold',
    fontSize: '11px',
  },
  warningChip: {
    backgroundColor: '#fff4e5',
    color: '#8a4b00',
    fontFamily: 'GilroySemiBold',
    fontSize: '11px',
  },
  errorChip: {
    backgroundColor: '#fdecea',
    color: '#b91c1c',
    fontFamily: 'GilroySemiBold',
    fontSize: '11px',
  },
  defaultChip: {
    backgroundColor: '#fff8e1',
    color: '#8d6e00',
    fontFamily: 'GilroySemiBold',
    fontSize: '11px',
  },
  monoCell: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#555',
  },
  dateCell: {
    whiteSpace: 'nowrap' as const,
    color: '#555',
    fontSize: 13,
  },
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px',
  },
};
